// src/pages/AdminDashboard.jsx — Dashboard Admin PayChat
import { useEffect, useState, useRef } from 'react';

const C = {
  bg:'#0A0E1A', surface:'#111827', card:'#1a2235', border:'#1e2d45',
  accent:'#00D4AA', gold:'#F5C542', red:'#FF4F6A', orange:'#F97316',
  green:'#10B981', blue:'#0EA5E9', text:'#E8EDF5', muted:'#6b7fa3',
};

const fmt = (n) => parseFloat(n||0).toLocaleString('fr-FR',{minimumFractionDigits:2,maximumFractionDigits:2});
const fmtInt = (n) => parseInt(n||0).toLocaleString('fr-FR');
const timeAgo = (d) => {
  if(!d) return '--';
  const s = Math.floor((Date.now()-new Date(d))/1000);
  if(s<60) return `il y a ${s}s`;
  if(s<3600) return `il y a ${Math.floor(s/60)}min`;
  if(s<86400) return `il y a ${Math.floor(s/3600)}h`;
  return new Date(d).toLocaleDateString('fr-FR');
};
const SEV = {high:C.red, medium:C.orange, low:C.gold, info:C.blue};
const TABS = ['🛡️ Sécurité','👥 Utilisateurs','🚨 Fraudes','📋 Audit','🌐 IPs Bannies','💰 Revenus'];
const TOKEN = () => localStorage.getItem('access_token');
const apiFetch = (path) => fetch('/api'+path,{headers:{Authorization:'Bearer '+TOKEN()}}).then(r=>r.json());
const apiPost = (path,body) => fetch('/api'+path,{method:'POST',headers:{'Content-Type':'application/json',Authorization:'Bearer '+TOKEN()},body:JSON.stringify(body)}).then(r=>r.json());

export function AdminDashboard() {
  const [tab, setTab] = useState(0);
  const [secStats, setSecStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [fraudAlerts, setFraudAlerts] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [bannedIPs, setBannedIPs] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [search, setSearch] = useState('');
  const [blockModal, setBlockModal] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [revenuePeriod, setRevenuePeriod] = useState(30);
  const timerRef = useRef(null);

  const toast = (msg, type='info') => {
    const id = Date.now();
    setToasts(p => [{id,msg,type,time:new Date()},...p.slice(0,8)]);
    setTimeout(()=>setToasts(p=>p.filter(t=>t.id!==id)),7000);
  };

  const loadRevenue = async (period) => {
    try {
      const rev = await apiFetch('/admin/revenue?period=' + period);
      if(rev.success) setRevenue(rev);
    } catch(e){}
  };

  const loadStats = async () => {
    try {
      const d = await apiFetch('/admin/security-stats');
      if(d.success) {
        setSecStats(d.data);
        if(parseInt(d.data.fraud_alerts_24h)>0) toast(`🚨 ${d.data.fraud_alerts_24h} alerte(s) fraude 24h !`,'danger');
      }
    } catch(e){}
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      await loadStats();
      const [u,fa,al,rev] = await Promise.all([
        apiFetch('/admin/users'),
        apiFetch('/admin/fraud-alerts'),
        apiFetch('/admin/audit-logs'),
        apiFetch('/admin/revenue?period=30'),
      ]);
      if(u.success) setUsers(u.data||[]);
      if(fa.success) setFraudAlerts(fa.data||[]);
      if(al.success) setAuditLogs(al.data||[]);
      if(rev.success) setRevenue(rev);
      try {
        const ips = await apiFetch('/admin/banned-ips');
        if(ips.success) {
          const raw = ips.data||'[]';
          const parsed = JSON.parse(raw.includes('[{') ? raw : '[]');
          setBannedIPs(parsed.flatMap(j=>Object.values(j).flat()));
        }
      } catch(e){}
      setLastRefresh(new Date());
    } catch(e){ console.error('loadAll error:',e); alert('ERREUR ADMIN: '+e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    // Activer le scroll sur body quand admin est actif
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
    document.getElementById('root').style.overflow = 'auto';
    document.getElementById('root').style.height = 'auto';
    loadAll();
    timerRef.current = setInterval(loadStats, 30000);
    return () => {
      clearInterval(timerRef.current);
      // Restaurer quand on quitte admin
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.getElementById('root').style.overflow = '';
      document.getElementById('root').style.height = '';
    };
  }, []);

  const blockUser = async () => {
    if(!blockModal) return;
    try {
      const r = await apiPost('/admin/block-user',{userId:blockModal.id,reason:blockReason});
      if(r.success) { toast(`🔒 ${blockModal.username} bloqué`,'success'); setBlockModal(null); setBlockReason(''); loadAll(); }
      else toast(r.error||'Erreur','danger');
    } catch(e){ toast('Erreur blocage','danger'); }
  };

  const unblockUser = async (userId, name) => {
    try {
      const r = await apiPost('/admin/unblock-user',{userId});
      if(r.success) { toast(`✅ ${name} débloqué`,'success'); loadAll(); }
    } catch(e){ toast('Erreur déblocage','danger'); }
  };

  const filtered = users.filter(u=>!search||
    u.username?.toLowerCase().includes(search.toLowerCase())||
    u.phone?.includes(search)||u.email?.toLowerCase().includes(search.toLowerCase()));

  const S = {
    page:{minHeight:'100vh',maxHeight:'100vh',background:C.bg,fontFamily:"'Sora',sans-serif",color:C.text,overflowY:'scroll',position:'relative'},
    hdr:{background:C.surface,borderBottom:`1px solid ${C.border}`,padding:'14px 28px',
      display:'flex',justifyContent:'space-between',alignItems:'center',position:'sticky',top:0,zIndex:100},
    card:{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:'20px 24px'},
    bdg:(c)=>({display:'inline-block',padding:'3px 10px',borderRadius:20,background:`${c}22`,color:c,fontSize:11,fontWeight:700,border:`1px solid ${c}44`}),
    btn:(c,bg)=>({padding:'7px 16px',borderRadius:8,border:`1px solid ${c}`,cursor:'pointer',background:bg||`${c}18`,color:c,fontWeight:700,fontSize:12,fontFamily:"'Sora',sans-serif"}),
    inp:{background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,padding:'8px 14px',color:C.text,fontSize:13,fontFamily:"'Sora',sans-serif",outline:'none',width:'100%'},
    tbl:{width:'100%',borderCollapse:'collapse'},
    th:{textAlign:'left',padding:'10px 14px',fontSize:11,color:C.muted,textTransform:'uppercase',letterSpacing:'0.08em',borderBottom:`1px solid ${C.border}`},
    td:{padding:'11px 14px',fontSize:13,borderBottom:`1px solid ${C.border}44`},
  };

  return (
    <div style={S.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&display=swap');*{box-sizing:border-box}::-webkit-scrollbar{width:4px}::-webkit-scrollbar-thumb{background:#1e2d45;border-radius:2px}.rh:hover{background:#111827!important}`}</style>

      {/* Header */}
      <div style={S.hdr}>
        <div>
          <div style={{fontSize:18,fontWeight:800}}>
            <span style={{color:C.accent}}>PAY</span><span style={{color:C.text}}>CHAT</span>
            <span style={{color:C.muted,fontSize:13,fontWeight:400}}> Admin</span>
          </div>
          <div style={{fontSize:11,color:C.muted}}>
            {lastRefresh?`Actualisé : ${lastRefresh.toLocaleTimeString('fr-FR')}`:'Chargement...'}
            <span style={{color:C.green,marginLeft:8}}>● Mouchard actif (30s)</span>
          </div>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <button onClick={loadAll} style={S.btn(C.accent)}>🔄 Rafraîchir</button>
          {secStats&&<>
            <span style={S.bdg(C.green)}>{secStats.active_users} actifs</span>
            {parseInt(secStats.fraud_alerts_24h)>0&&<span style={S.bdg(C.red)}>🚨 {secStats.fraud_alerts_24h}</span>}
            {parseInt(secStats.blocked_users)>0&&<span style={S.bdg(C.orange)}>🔒 {secStats.blocked_users}</span>}
          </>}
        </div>
      </div>

      {/* Toasts */}
      {toasts.length>0&&(
        <div style={{position:'fixed',top:72,right:20,zIndex:999,display:'flex',flexDirection:'column',gap:8,maxWidth:360,pointerEvents:'none'}}>
          {toasts.map(t=>(
            <div key={t.id} style={{background:t.type==='danger'?'#1a0a0e':t.type==='success'?'#0a1a12':'#1a140a',border:`1px solid ${t.type==='danger'?C.red:t.type==='success'?C.green:C.orange}`,borderRadius:10,padding:'10px 16px',fontSize:13,color:C.text}}>
              {t.msg}
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{background:C.surface,borderBottom:`1px solid ${C.border}`,padding:'0 28px',display:'flex',gap:2,overflowX:'auto'}}>
        {TABS.map((t,i)=>(
          <button key={i} onClick={()=>setTab(i)} style={{padding:'12px 16px',border:'none',cursor:'pointer',background:'transparent',fontFamily:"'Sora',sans-serif",fontSize:13,fontWeight:tab===i?700:400,color:tab===i?C.accent:C.muted,whiteSpace:'nowrap',borderBottom:tab===i?`2px solid ${C.accent}`:'2px solid transparent'}}>
            {t}
          </button>
        ))}
      </div>

      {loading&&<div style={{padding:60,textAlign:'center',color:C.muted,fontSize:14}}>Chargement... ({users.length} users en mémoire)</div>}

      {!loading&&(
        <div style={{padding:'24px 28px',maxWidth:1500,margin:'0 auto'}}>

          {/* TAB 0 — SÉCURITÉ */}
          {tab===0&&(
            <div style={{display:'flex',flexDirection:'column',gap:20}}>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:14}}>
                {[
                  {l:'Utilisateurs actifs',v:secStats?.active_users||0,c:C.accent,i:'👥'},
                  {l:'Alertes fraude 24h',v:secStats?.fraud_alerts_24h||0,c:parseInt(secStats?.fraud_alerts_24h)>0?C.red:C.green,i:'🚨'},
                  {l:'Comptes bloqués',v:secStats?.blocked_users||0,c:parseInt(secStats?.blocked_users)>0?C.orange:C.green,i:'🔒'},
                  {l:"Transactions aujourd'hui",v:secStats?.tx_today||0,c:C.blue,i:'💳'},
                  {l:'Tx échouées',v:secStats?.failed_tx_today||0,c:parseInt(secStats?.failed_tx_today)>5?C.red:C.muted,i:'❌'},
                ].map((k,i)=>(
                  <div key={i} style={{...S.card,borderLeft:`4px solid ${k.c}`,padding:'18px 20px'}}>
                    <div style={{fontSize:22,marginBottom:6}}>{k.i}</div>
                    <div style={{fontSize:30,fontWeight:800,color:k.c}}>{fmtInt(k.v)}</div>
                    <div style={{fontSize:11,color:C.muted,marginTop:4}}>{k.l}</div>
                  </div>
                ))}
              </div>
              <div style={S.card}>
                <div style={{fontSize:15,fontWeight:700,marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span>🚨 Dernières alertes fraude</span>
                  <button style={S.btn(C.accent)} onClick={()=>setTab(2)}>Voir tout</button>
                </div>
                {fraudAlerts.slice(0,5).length===0
                  ?<div style={{color:C.green,fontSize:13}}>✅ Aucune fraude détectée</div>
                  :<table style={S.tbl}><thead><tr>
                    <th style={S.th}>Utilisateur</th><th style={S.th}>Montant</th><th style={S.th}>Alertes</th><th style={S.th}>Date</th><th style={S.th}>Action</th>
                  </tr></thead><tbody>
                  {fraudAlerts.slice(0,5).map((fa,i)=>(
                    <tr key={i} className="rh">
                      <td style={S.td}><div style={{fontWeight:600}}>{fa.display_name||fa.username}</div><div style={{fontSize:11,color:C.muted}}>{fa.phone}</div></td>
                      <td style={{...S.td,color:C.gold,fontWeight:700}}>{fmtInt(fa.amount)} FCFA</td>
                      <td style={S.td}>{(fa.alerts||[]).map((a,j)=><span key={j} style={{...S.bdg(SEV[a.severity]||C.muted),marginRight:4}}>{a.code}</span>)}</td>
                      <td style={{...S.td,color:C.muted,fontSize:12}}>{timeAgo(fa.created_at)}</td>
                      <td style={S.td}>{fa.is_active?<button style={S.btn(C.red)} onClick={()=>setBlockModal({...fa,id:fa.user_id})}>🔒</button>:<button style={S.btn(C.green)} onClick={()=>unblockUser(fa.user_id,fa.username)}>🔓</button>}</td>
                    </tr>
                  ))}</tbody></table>}
              </div>
              <div style={S.card}>
                <div style={{fontSize:15,fontWeight:700,marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span>📋 Activité récente</span>
                  <button style={S.btn(C.blue)} onClick={()=>setTab(3)}>Journal complet</button>
                </div>
                <table style={S.tbl}><thead><tr>
                  <th style={S.th}>Utilisateur</th><th style={S.th}>Action</th><th style={S.th}>IP</th><th style={S.th}>Date</th>
                </tr></thead><tbody>
                {auditLogs.slice(0,8).map((al,i)=>(
                  <tr key={i} className="rh">
                    <td style={S.td}><div style={{fontWeight:600,fontSize:13}}>{al.display_name||al.username||'Système'}</div></td>
                    <td style={S.td}><span style={{color:C.accent,fontFamily:'monospace',fontSize:12}}>{al.action}</span></td>
                    <td style={{...S.td,fontFamily:'monospace',fontSize:12,color:C.muted}}>{al.ip_address||'--'}</td>
                    <td style={{...S.td,color:C.muted,fontSize:12}}>{timeAgo(al.created_at)}</td>
                  </tr>
                ))}</tbody></table>
              </div>
            </div>
          )}

          {/* TAB 1 — UTILISATEURS */}
          {tab===1&&(
            <div style={{display:'flex',flexDirection:'column',gap:16}}>
              <div style={{display:'flex',gap:12,alignItems:'center'}}>
                <input style={{...S.inp,maxWidth:340}} placeholder="🔍 Nom, téléphone, email..." value={search} onChange={e=>setSearch(e.target.value)}/>
                <span style={{color:C.muted,fontSize:13,fontWeight:600}}>{filtered.length} / {users.length} utilisateur(s)</span>
              </div>
              <div style={S.card}>
                {users.length===0?<div style={{color:C.muted,padding:20,textAlign:'center'}}>Aucun utilisateur chargé</div>:
                <table style={S.tbl}><thead><tr>
                  <th style={S.th}>Utilisateur</th><th style={S.th}>Téléphone</th><th style={S.th}>Solde</th>
                  <th style={S.th}>Tx</th><th style={S.th}>Fraudes</th><th style={S.th}>Activité</th>
                  <th style={S.th}>Statut</th><th style={S.th}>Action</th>
                </tr></thead><tbody>
                {filtered.map((u,i)=>(
                  <tr key={u.id} className="rh" style={{background:!u.is_active?'#1a0808':undefined}}>
                    <td style={S.td}>
                      <div style={{fontWeight:600,color:u.is_active?C.text:C.red}}>{u.display_name||u.username}</div>
                      <div style={{fontSize:11,color:C.muted}}>@{u.username}</div>
                    </td>
                    <td style={{...S.td,fontFamily:'monospace',fontSize:11}}>{u.phone}</td>
                    <td style={{...S.td,color:C.gold,fontWeight:700}}>{fmtInt(u.balance||0)} FCFA</td>
                    <td style={{...S.td,textAlign:'center'}}>{u.tx_count||0}</td>
                    <td style={{...S.td,textAlign:'center',color:parseInt(u.fraud_count)>0?C.red:C.muted}}>{parseInt(u.fraud_count)>0?`🚨 ${u.fraud_count}`:'--'}</td>
                    <td style={{...S.td,color:C.muted,fontSize:12}}>{timeAgo(u.last_activity)}</td>
                    <td style={S.td}>{u.is_active?<span style={S.bdg(C.green)}>Actif</span>:<span style={S.bdg(C.red)}>Bloqué</span>}</td>
                    <td style={S.td}>{u.is_active
                      ?<button style={{...S.btn(C.red),padding:'5px 12px'}} onClick={()=>setBlockModal(u)}>🔒</button>
                      :<button style={{...S.btn(C.green),padding:'5px 12px'}} onClick={()=>unblockUser(u.id,u.username)}>🔓</button>}
                    </td>
                  </tr>
                ))}</tbody></table>}
              </div>
            </div>
          )}

          {/* TAB 2 — FRAUDES */}
          {tab===2&&(
            <div style={S.card}>
              <div style={{fontSize:15,fontWeight:700,marginBottom:16}}>🚨 Alertes fraude ({fraudAlerts.length})</div>
              {fraudAlerts.length===0?<div style={{color:C.green,padding:'20px 0'}}>✅ Aucune alerte</div>:
              <table style={S.tbl}><thead><tr>
                <th style={S.th}>Utilisateur</th><th style={S.th}>Téléphone</th><th style={S.th}>Montant</th>
                <th style={S.th}>Détails</th><th style={S.th}>Sévérité</th><th style={S.th}>Date</th><th style={S.th}>Action</th>
              </tr></thead><tbody>
              {fraudAlerts.map((fa,i)=>{
                const als=fa.alerts||[];
                const ms=als.some(a=>a.severity==='high')?'high':als.some(a=>a.severity==='medium')?'medium':'low';
                return(<tr key={i} className="rh" style={{background:ms==='high'?'#1a090a':undefined}}>
                  <td style={S.td}><div style={{fontWeight:600}}>{fa.display_name||fa.username}</div></td>
                  <td style={{...S.td,fontFamily:'monospace',fontSize:11}}>{fa.phone}</td>
                  <td style={{...S.td,color:C.gold,fontWeight:700}}>{fmtInt(fa.amount)} FCFA</td>
                  <td style={S.td}>{als.map((a,j)=><div key={j} style={{fontSize:11,color:SEV[a.severity]||C.muted}}>{a.code}: {a.message}</div>)}</td>
                  <td style={S.td}><span style={S.bdg(SEV[ms]||C.muted)}>{ms}</span></td>
                  <td style={{...S.td,color:C.muted,fontSize:11}}>{new Date(fa.created_at).toLocaleString('fr-FR')}</td>
                  <td style={S.td}>{fa.is_active?<button style={S.btn(C.red)} onClick={()=>setBlockModal({...fa,id:fa.user_id})}>🔒</button>:<button style={S.btn(C.green)} onClick={()=>unblockUser(fa.user_id,fa.username)}>🔓</button>}</td>
                </tr>);
              })}</tbody></table>}
            </div>
          )}

          {/* TAB 3 — AUDIT */}
          {tab===3&&(
            <div style={S.card}>
              <div style={{fontSize:15,fontWeight:700,marginBottom:16}}>📋 Journal d'audit ({auditLogs.length} entrées)</div>
              <table style={S.tbl}><thead><tr>
                <th style={S.th}>Utilisateur</th><th style={S.th}>Action</th><th style={S.th}>Entité</th><th style={S.th}>IP</th><th style={S.th}>Date</th>
              </tr></thead><tbody>
              {auditLogs.map((al,i)=>(
                <tr key={i} className="rh">
                  <td style={S.td}><div style={{fontWeight:600,fontSize:13}}>{al.display_name||al.username||'Système'}</div></td>
                  <td style={S.td}><span style={{color:al.action?.includes('block')?C.red:C.accent,fontFamily:'monospace',fontSize:12}}>{al.action}</span></td>
                  <td style={{...S.td,fontSize:12,color:C.muted}}>{al.entity_type||'--'}</td>
                  <td style={{...S.td,fontFamily:'monospace',fontSize:12,color:C.blue}}>{al.ip_address||'--'}</td>
                  <td style={{...S.td,color:C.muted,fontSize:12}}>{timeAgo(al.created_at)}</td>
                </tr>
              ))}</tbody></table>
            </div>
          )}

          {/* TAB 4 — IPs BANNIES */}
          {tab===4&&(
            <div style={{display:'flex',flexDirection:'column',gap:16}}>
              <div style={{...S.card,borderLeft:`4px solid ${C.red}`}}>
                <div style={{fontSize:15,fontWeight:700,marginBottom:4}}>🌐 IPs bannies par Fail2Ban</div>
                <div style={{fontSize:12,color:C.muted,marginBottom:16}}>{bannedIPs.length} adresse(s) · Ban 24-72h</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                  {bannedIPs.length===0?<span style={{color:C.muted}}>Aucune IP bannie</span>:bannedIPs.map((ip,i)=>(
                    <span key={i} style={{background:`${C.red}15`,border:`1px solid ${C.red}44`,borderRadius:6,padding:'4px 10px',fontSize:12,fontFamily:'monospace',color:C.red}}>{ip}</span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 5 — REVENUS */}
          {tab===5&&(
            <div style={{display:'flex',flexDirection:'column',gap:20}}>
              {/* Sélecteur de période */}
              <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                <span style={{fontSize:13,color:C.muted,fontWeight:600}}>Période :</span>
                {[
                  {label:'Aujourd\'hui',value:1},
                  {label:'7 jours',value:7},
                  {label:'30 jours',value:30},
                  {label:'90 jours',value:90},
                ].map(opt=>(
                  <button key={opt.value}
                    onClick={()=>{setRevenuePeriod(opt.value);loadRevenue(opt.value);}}
                    style={{
                      padding:'7px 18px',borderRadius:8,border:'none',cursor:'pointer',
                      fontWeight:700,fontSize:13,transition:'all .15s',
                      background: revenuePeriod===opt.value ? C.accent : `${C.accent}18`,
                      color: revenuePeriod===opt.value ? '#000' : C.accent,
                    }}
                  >{opt.label}</button>
                ))}
                <span style={{fontSize:11,color:C.muted,marginLeft:'auto'}}>
                  {revenuePeriod===1?'Données du jour':revenuePeriod===7?'7 derniers jours':revenuePeriod===30?'30 derniers jours':'90 derniers jours'}
                </span>
              </div>

              {revenue?<>
                {/* Métriques principales */}
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:14}}>
                  {[
                    {l:'Revenus commissions',v:`FCFA ${fmt(revenue.totals?.total_fees)}`,c:C.accent},
                    {l:'Volume traité',v:`FCFA ${fmt(revenue.totals?.total_volume)}`,c:C.blue},
                    {l:'Transactions',v:fmtInt(revenue.totals?.total_transactions),c:C.gold},
                    {l:'Utilisateurs actifs',v:fmtInt(revenue.totals?.active_users),c:C.green},
                  ].map((k,i)=>(
                    <div key={i} style={{...S.card,borderLeft:`4px solid ${k.c}`}}>
                      <div style={{fontSize:11,color:C.muted,marginBottom:6}}>{k.l}</div>
                      <div style={{fontSize:26,fontWeight:800,color:k.c}}>{k.v}</div>
                      <div style={{fontSize:11,color:C.muted,marginTop:4}}>
                        {revenuePeriod===1?'Aujourd\'hui':revenuePeriod===7?'/ 7 jours':revenuePeriod===30?'/ 30 jours':'/ 90 jours'}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Graphique évolution */}
                <div style={S.card}>
                  <div style={{fontSize:14,fontWeight:700,marginBottom:4}}>
                    Évolution {revenuePeriod===1?'horaire':revenuePeriod===7?'sur 7 jours':revenuePeriod===30?'sur 30 jours':'sur 90 jours'}
                  </div>
                  <div style={{fontSize:11,color:C.muted,marginBottom:14}}>
                    Commissions encaissées (FCFA)
                  </div>
                  <div style={{display:'flex',alignItems:'flex-end',gap:3,height:120}}>
                    {(revenue.daily_evolution||[]).map((d,i)=>{
                      const mx=Math.max(...(revenue.daily_evolution||[{fees:1}]).map(x=>parseFloat(x.fees)||0));
                      const h=mx>0?Math.max(4,parseFloat(d.fees||0)/mx*110):4;
                      const isLast=i===(revenue.daily_evolution.length-1);
                      return (
                        <div key={i} style={{flex:1,minWidth:revenuePeriod===1?18:revenuePeriod===7?40:6,display:'flex',flexDirection:'column',alignItems:'center',gap:2}}>
                          <div
                            style={{
                              width:'100%',height:h,
                              background:isLast?`linear-gradient(180deg,${C.accent},#00b894)`:`linear-gradient(180deg,${C.accent}88,${C.accent}44)`,
                              borderRadius:'3px 3px 0 0',
                              transition:'height .3s'
                            }}
                            title={`${d.date||d.hour||''}: FCFA ${fmt(d.fees)}`}
                          />
                          {revenuePeriod<=7&&(
                            <div style={{fontSize:9,color:C.muted,textAlign:'center',whiteSpace:'nowrap'}}>
                              {d.date?new Date(d.date).toLocaleDateString('fr-FR',{day:'2-digit',month:'2-digit'}):d.hour||''}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {(revenue.daily_evolution||[]).length===0&&(
                    <div style={{color:C.muted,textAlign:'center',padding:20,fontSize:13}}>
                      Aucune transaction sur cette période
                    </div>
                  )}
                </div>

                {/* Résumé textuel */}
                <div style={{...S.card,display:'flex',gap:24,flexWrap:'wrap'}}>
                  <div>
                    <div style={{fontSize:11,color:C.muted,marginBottom:4}}>Commission moyenne / transaction</div>
                    <div style={{fontSize:18,fontWeight:700,color:C.accent}}>
                      FCFA {revenue.totals?.total_transactions>0?fmt(revenue.totals?.total_fees/revenue.totals?.total_transactions):'0'}
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:C.muted,marginBottom:4}}>Taux de commission moyen</div>
                    <div style={{fontSize:18,fontWeight:700,color:C.gold}}>
                      {revenue.totals?.total_volume>0?(revenue.totals?.total_fees/revenue.totals?.total_volume*100).toFixed(2):'0'}%
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:C.muted,marginBottom:4}}>Volume moyen / transaction</div>
                    <div style={{fontSize:18,fontWeight:700,color:C.blue}}>
                      FCFA {revenue.totals?.total_transactions>0?fmt(revenue.totals?.total_volume/revenue.totals?.total_transactions):'0'}
                    </div>
                  </div>
                </div>
              </>:<div style={{color:C.muted,textAlign:'center',padding:40}}>Données revenus non disponibles</div>}
            </div>
          )}
        </div>
      )}

      {/* Modal blocage */}
      {blockModal&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:200}}>
          <div style={{...S.card,width:420,border:`1px solid ${C.red}`}}>
            <div style={{fontSize:16,fontWeight:800,marginBottom:4,color:C.red}}>🔒 Bloquer l'utilisateur</div>
            <div style={{fontSize:13,color:C.muted,marginBottom:16}}><strong style={{color:C.text}}>{blockModal.display_name||blockModal.username}</strong> — {blockModal.phone}</div>
            <div style={{fontSize:12,color:C.muted,marginBottom:6}}>Raison :</div>
            <input style={{...S.inp,marginBottom:20}} placeholder="Ex: Activité suspecte..." value={blockReason} onChange={e=>setBlockReason(e.target.value)}/>
            <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
              <button style={S.btn(C.muted,'transparent')} onClick={()=>{setBlockModal(null);setBlockReason('');}}>Annuler</button>
              <button style={{...S.btn(C.red),background:`${C.red}22`}} onClick={blockUser}>Confirmer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
